<template>
  <div class="vhub-chart-wrapper">
    <button v-if="!noCloseButton" class="add-button" @click="$emit('close')">
      <Icon>{{ closeMedium }}</Icon>
      <span>{{ $t('message.HIDE_CHART') }}</span>
    </button>
    <div class="title">
      <span>{{ chartTitle }}</span>
    </div>
    <Checkbox
      v-if="allowFilter"
      v-model="displayAllGroups"
      :title="$t('message.DISPLAY_ALL_GROUPS')"
      class="all-groups"
    />
    <Switcher v-if="allChartsData.length > 1" v-model="chartIndex" :titles="allChartsNames" />
    <slot v-if="useCanvasSlot" />
    <canvas v-else ref="chartCanvas" />
  </div>
</template>

<script>
/**
 * Component used to display a chart related to Vitality scores.
 *
 * @vue-prop {String} chartTitle - Chart legend
 * @vue-prop {String} chartScoresKey - key of the scores that should be displayed on the chart.
 * @vue-prop {Object} [criterion=null] - If the scores refer to a criterion, this is the prop to specify it
 * @vue-prop {String} [groups=null] - If set and display only the groups within this array by default
 * @vue-prop {Boolean} [noCloseButton] - Set to true to hide the close button
 * @vue-prop {Boolean} [useCanvasSlot] - True to remove the default canvas from this component and to use a slot
 */

import $ from 'jquery';
import { Chart } from 'chart.js';
import { isEqual, cloneDeep } from 'lodash';
// Vuex
import { createNamespacedHelpers } from 'vuex';
// Components
import Checkbox from '@/components/checkbox.vue';
import Icon from '@/components/icon.vue';
import Switcher from '@/components/switcher.vue';
// Utils
import { closeMedium } from '@/assets/icons';
import { defaultChartOptions, defaultDatasetProps, VHUB_NUMBER_OF_CALCULATED_PERIODS } from '@/utils/vhub-utils';

const { mapGetters, mapState } = createNamespacedHelpers('vitality');


export default {
  name: 'VHubChartWrapper',

  components: {
    Checkbox,
    Icon,
    Switcher,
  },

  props: {
    chartTitle: {
      type: String,
      required: true,
    },
    chartScoresKey: {
      type: String,
      required: true,
    },
    criterion: {
      type: Object,
      default: null,
    },
    groups: {
      type: Array,
      default: null,
    },
    noCloseButton: Boolean,
    useCanvasSlot: Boolean,
  },

  data() {
    return {
      closeMedium,
      chart: null,
      chartBuildTimeout: null,
      // If false, use groups
      displayAllGroups: false,
    };
  },

  computed: {
    /** Boolean: Is the array of teams filterable */
    allowFilter() {
      return this.groups;
    },
    /** Array: Same as this.ranges without the first range which doesn't show any value */
    visibleRanges() {
      if (!(this.ranges instanceof Array)) {
        return [];
      }
      return this.ranges.filter((r, i) => i < VHUB_NUMBER_OF_CALCULATED_PERIODS);
    },
    /** Array: ranges displayed as strings */
    chartFormattedRanges() {
      if (!(this.ranges instanceof Array)) {
        return [];
      }
      const ranges = cloneDeep(this.visibleRanges);
      if (this.rangeIsMonth) {
        return ranges.reverse().map(range => range.start.format('MMM'));
      }
      return ranges.reverse().map(({ start, end }) => {
        const df = this.$t('minDateFormat');
        return `${start.format(df)} - ${end.format(df)}`;
      });
    },
    /**
     * Index of the cart displayed amongst available charts whose data and options can be found in
     * 'allChartsData' and 'allChartsOptions'
     * @type {Number}
     */
    chartIndex: {
      get() {
        if (!this.criterion) {
          return 0;
        }
        let idx = 0;
        const qpIndex = this.$route.query[`${this.criterion.key}chartIndex`];
        if (qpIndex !== undefined) {
          idx = Number(qpIndex);
        } else if (this.criterion) {
          idx = this.criterion.defaultChartIndex || 0;
        }
        return Math.min(idx, this.allChartsData.length - 1);
      },
      set(newVal) {
        const query = Object.assign({}, this.$route.query);
        query[`${this.criterion.key}chartIndex`] = Math.min(newVal, this.allChartsData.length - 1);
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    showChart() {
      return this.chartScoresKey !== '';
    },
    /** Array: groups after using groupsFilter function */
    filteredTeamGroups() {
      if (this.groups && !this.displayAllGroups) {
        return this.groups;
      }
      return this.teamGroups;
    },
    // ========================================================================================
    // This wrapper may hold multiple charts as criteria may need to display additionnal charts
    // Computed properties below allow this feature by fetching criteria custom chart data
    // ========================================================================================
    /** Array: chart.js datasets and labels of diplayable charts */
    allChartsNames() {
      const customChartName = this.criterion && this.criterion.chartNames();
      if (customChartName) {
        if (customChartName instanceof Array) {
          return [this.defaultChartName, ...customChartName];
        }
        return [this.defaultChartName, customChartName];
      }
      return [this.defaultChartName];
    },
    /** Object: chart data used when only one chart displayed or index = 0 */
    defaultChartName() {
      return this.$t('message.VH_DEFAULT_CHART_NAME');
    },
    /** Array: chart.js datasets and labels of diplayable charts */
    allChartsData() {
      let customChartData = this.criterion && this.criterion.chartData(this.filteredTeamGroups);
      if (customChartData) {
        if (!(customChartData instanceof Array)) {
          customChartData = [customChartData];
        }
        customChartData = customChartData.map((data) => {
          const labels = data.labels ? data.labels : this.chartFormattedRanges;
          return { ...data, labels };
        });
        return [this.defaultChartData, ...customChartData];
      }
      return [this.defaultChartData];
    },
    /** Object: chart data formatted as chart.js requires */
    chartData() {
      return this.allChartsData[this.chartIndex];
    },
    /** Object: chart data used when only one chart displayed or index = 0 */
    defaultChartData() {
      // Generic chart, one value per period
      const datasets = [];
      this.filteredTeamGroups.forEach((group) => {
        const data = group[this.chartScoresKey];
        if (data) {
          const reverseData = cloneDeep(data).reverse();
          datasets.push({
            ...defaultDatasetProps,
            borderColor: group.chartColor,
            label: group.name,
            data: reverseData.map(v => (v.score !== undefined ? v.score.sToFixed(1) : v.sToFixed(1))),
            scoreData: reverseData.map(v => v.data),
          });
        }
      });
      return {
        labels: this.chartFormattedRanges,
        datasets,
      };
    },
    /** Array: chart.js options of diplayable charts */
    allChartsOptions() {
      const opts = this.criterion && this.criterion.chartOptions();
      if (opts) {
        // Custom charts for this criterion
        if (opts instanceof Array) {
          return [this.defaultChartOptions, ...opts];
        }
        return [this.defaultChartOptions, opts];
      }
      return [this.defaultChartOptions];
    },
    chartOptions() {
      return this.allChartsOptions[this.chartIndex];
    },
    /** Object: Chart.js options of the default chart */
    defaultChartOptions() {
      return defaultChartOptions(this.criterion, this.filteredTeamGroups.length < 2);
    },
    ...mapState([
      'ranges',
      'teamGroups',
    ]),
    ...mapGetters([
      'rangeIsMonth',
    ]),
  },

  watch: {
    chartData(newVal, oldVal) {
      if (this.showChart && !isEqual(newVal, oldVal)) {
        this.delayBuild();
      }
    },
    filteredTeamGroups(newVal, oldVal) {
      if (this.showChart && !isEqual(newVal, oldVal)) {
        this.delayBuild();
      }
    },
  },

  created() {
    this.delayBuild();
    $(window).on(`resizeEnd.${this.uuid}`, this.delayBuild);
  },

  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy();
    }
    clearTimeout(this.chartBuildTimeout);
  },

  methods: {
    delayBuild() {
      clearTimeout(this.chartBuildTimeout);
      this.chartBuildTimeout = setTimeout(this.buildChart, 150);
    },
    /**
     * Score columns can be clicked to display a chart
     * @param  {Array} values - Array of number values
     */
    buildChart() {
      if (this.useCanvasSlot) {
        this.$emit('build', this.chartFormattedRanges);
        return;
      }
      const { chartCanvas } = this.$refs;
      if (!chartCanvas) {
        return;
      }
      if (this.chart) {
        this.chart.destroy();
      }
      chartCanvas.width = chartCanvas.offsetWidth;
      chartCanvas.height = chartCanvas.offsetHeight;

      this.chart = new Chart(chartCanvas.getContext('2d'), {
        type: 'line',
        data: this.chartData,
        options: this.chartOptions,
      });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.vhub-chart-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-flow: column;

  .add-button {
    position: absolute;
    top: 0px;
    left: 0px;
    padding: 3px 5px;
    font-size: 0.9em;

    &:hover {
      background-color: $color-hoverbtn-red;
    }
  }
  .title {
    position: relative;
    flex: 0 0 2em;
    text-align: center;
    font-weight: 600;
    color: $color-blue-text;
    display: inline;
    pointer-events: none;

    span {
      padding: 3px 0px;
      background: white;
      box-shadow: 0px 0px 7px 7px white;
    }
  }
  .checkbox.all-groups {
    margin: auto;
  }
  canvas {
    flex-grow: 1;
    box-sizing: border-box;
    width: 100%;
    height: calc(100% - 2em);
  }
}
</style>
